import { DisclosureGate } from "@/components/common/DisclosureGate";
import { ModuleRoute } from "@/components/common/ModuleRoute";
import { EmailSignIn } from "@/components/common/EmailSignIn";
import { AppShell } from "@/components/layout/AppShell";
import { BrandBar } from "@/components/layout/BrandBar";
import { OrgLockup } from "@/components/layout/OrgLockup";
import { Button } from "@/components/ui/button";
import { useBranding, useOrg } from "@/hooks";
import { DEFAULT_PATH } from "@/lib/nav";
import { ActivityPage } from "@/routes/activity";
import { BillingPage } from "@/routes/billing";
import { ContentPage } from "@/routes/content";
import { PeoplePage } from "@/routes/people";
import { AnalyticsPage } from "@/routes/analytics";
import { AnalyticsMemberPage } from "@/routes/analyticsMember";
import { SeatsPage } from "@/routes/seats";
import { SettingsPage } from "@/routes/settings";
import { SetupPage } from "@/routes/setup";
import { TeamsPage } from "@/routes/teams";
import { ApiError, authService } from "@/services";
import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

/**
 * Gate for the whole app. Three outcomes, and they need different screens:
 *   - signed out            → sign in
 *   - signed in, no seat    → "you don't have access to a dashboard"
 *   - signed in with a seat → the app
 *
 * Collapsing the middle case into "sign in" would send an already-signed-in
 * person round a loop that can never succeed.
 */
export function App() {
	const [booting, setBooting] = useState(true);
	const [signedIn, setSignedIn] = useState(false);

	useEffect(() => {
		authService.restore().then((user) => {
			setSignedIn(!!user);
			setBooting(false);
		});
	}, []);

	if (booting) return <Centered>Loading…</Centered>;
	if (!signedIn) return <SignIn />;

	// Every data query lives inside this component, so none of them can mount
	// before the access token exists. Calling useOrg() alongside the auth effect
	// fired it on the first render with no token: it 401'd, and the query client
	// deliberately does not retry a 401, so it never recovered — a blank org
	// name and a stuck skeleton, with a perfectly healthy API behind it.
	return <AuthedApp />;
}

function AuthedApp() {
	const org = useOrg();

	if (org.error instanceof ApiError && org.error.isNotOrgMember) {
		return (
			<Centered>
				<h1 className="text-h5 font-semibold text-heading">
					No dashboard access
				</h1>
				<p className="max-w-sm text-body text-muted-foreground">
					This account isn't an administrator of any organization. If you expect
					access, ask your organization's owner to invite you.
				</p>
				<SignOutButton />
			</Centered>
		);
	}

	// Any other failure — the API down, a bad gateway — is not "no access", and
	// saying so would send someone chasing permissions they already have.
	if (org.error) {
		return (
			<Centered>
				<h1 className="text-h5 font-semibold text-heading">Can't reach Curo</h1>
				<p className="max-w-sm text-body text-muted-foreground">
					{org.error instanceof ApiError
						? org.error.message
						: "Something went wrong loading your organization."}
				</p>
				<Button variant="outline" onClick={() => location.reload()}>
					Try again
				</Button>
			</Centered>
		);
	}

	// AN ORG THAT HAS NOT BEEN SET UP HAS NOTHING TO SHOW YET.
	//
	// Staff create it with the partner's nominated email as owner and nothing
	// else decided — no seat count, no contract, no invoice. Dropping that owner
	// into the normal dashboard shows them an empty roster and a Billing screen
	// with no plan, and no hint that the next move is theirs. The setup screen
	// IS the next move.
	//
	// Checked before the suspension branch because an org that never started
	// cannot be suspended for non-payment of an invoice that was never issued.
	if (org.data && !org.data.org.contract_starts_at) {
		return (
			<AppShell>
				<SetupPage />
			</AppShell>
		);
	}

	// A SUSPENDED ORG KEEPS EXACTLY ONE SCREEN: Billing.
	//
	// The server allows /me and /billing/* while suspended and refuses the rest,
	// so the app must route accordingly — otherwise every other tab 403s and the
	// admin sees a broken dashboard instead of the one thing they can act on.
	// Suspension is reversible the moment the invoice is paid, so the screen
	// that takes payment is the last thing to take away.
	if (org.data?.org.status === "suspended") {
		return (
			<AppShell>
				<div className="mb-6 rounded-md border border-error-stroke bg-error-surface px-4 py-3">
					<h1 className="text-title2 font-semibold text-error">
						Access is paused for your team
					</h1>
					<p className="mt-1 max-w-xl text-body text-error">
						An invoice is past due, so Curo is paused for everyone in your
						organization.{" "}
						<span className="font-medium">Nothing has been deleted.</span> Pay
						below and everyone is restored immediately — seats, plans, notes and
						history all intact.
					</p>
				</div>
				<BillingPage />
			</AppShell>
		);
	}

	// Every org member passes the disclosure once before reaching anything —
	// including admins, who are members too and whose own activity is visible
	// to their owners.
	return (
		<DisclosureGate>
			<AppShell>
				<Routes>
					<Route path="/" element={<Navigate to={DEFAULT_PATH} replace />} />
					<Route path="/seats" element={<SeatsPage />} />
					<Route path="/people" element={<PeoplePage />} />
					<Route path="/analytics" element={<AnalyticsPage />} />
					<Route path="/analytics/:memberId" element={<AnalyticsMemberPage />} />
					<Route
						path="/teams"
						element={
							<ModuleRoute module="teams" role={org.data?.org.role}>
								<TeamsPage />
							</ModuleRoute>
						}
					/>
					<Route path="/content" element={<ContentPage />} />
					<Route path="/billing" element={<BillingPage />} />
					<Route path="/activity" element={<ActivityPage />} />
					<Route path="/settings" element={<SettingsPage />} />
					<Route path="*" element={<Navigate to={DEFAULT_PATH} replace />} />
				</Routes>
			</AppShell>
		</DisclosureGate>
	);
}

/**
 * Branding here comes from the HOSTNAME, not a session — there isn't one yet.
 * An unrecognised host is not an error: it just renders as plain Curo, because
 * a DNS entry pointing somewhere unexpected should never make sign-in unusable.
 */
function SignIn() {
	const { data: brand } = useBranding();
	return (
		<>
			<BrandBar accent={brand?.accent} />
			<Centered>
				{brand?.logo_url ? (
					<img
						src={brand.logo_url}
						alt=""
						className="mb-1 h-10 w-10 rounded-lg object-contain"
					/>
				) : null}
				{/* The same lockup the app uses, so signing in and being signed
				    in are visibly the same product. */}
				{brand?.name ? (
					<OrgLockup name={brand.name} className="text-h5" />
				) : (
					<h1 className="text-h5 font-semibold text-heading">Curo for teams</h1>
				)}
				<p className="max-w-sm text-body text-muted-foreground">
					{brand
						? `Sign in with the account ${brand.name} invited.`
						: "Sign in with the account your organization invited."}
				</p>
				<Button onClick={() => authService.startGoogleSignIn()}>
					Continue with Google
				</Button>

				{/*
				 * Not every partner is on Google. Until OIDC lands, an emailed
				 * code is what keeps a company on Microsoft or anything else
				 * from being locked out entirely.
				 */}
				<div className="flex w-full max-w-xs items-center gap-3">
					<div className="h-px flex-1 bg-divider" />
					<span className="text-caption text-muted-foreground">or</span>
					<div className="h-px flex-1 bg-divider" />
				</div>
				<EmailSignIn onSignedIn={() => location.reload()} />
			</Centered>
		</>
	);
}

function SignOutButton() {
	return (
		<Button
			variant="outline"
			onClick={() => authService.signOut().then(() => location.reload())}
		>
			Sign out
		</Button>
	);
}

function Centered({ children }: { children: React.ReactNode }) {
	return (
		<div className="flex min-h-[calc(100vh-3px)] flex-col items-center justify-center gap-3 bg-surface2 px-6 text-center">
			{children}
		</div>
	);
}
